import React from "react";
import { Icon } from "./siIcons";
import { siApi } from "./siApi";
import { aFaire, aujourdhui, fmtD, fmtTs, initiales, trier } from "./siModel";

// Onglet « Utilisateurs » de l'écran S.I — maquette claude-design/si.jsx,
// branchée sur l'API (backend controllers/siController.js). Les comptes
// MyALFA remontent automatiquement ; les utilisateurs « hors système »
// (prestataires, intérimaires…) sont saisis ici. Mises à jour immédiates à
// l'écran, annulées si le serveur refuse.

const CHAMPS_FICHE = ["site", "service", "domaine", "mail", "rustdesk", "refInterne", "nomPc", "dateArrivee", "dateSortie", "note"];
const CHAMPS_EXTERNE = ["nom", "prenom", "fonction"];
const nomExterne = (d) => [(d.nom || "").toUpperCase(), d.prenom || ""].join(" ").trim();

// Champs modifiés entre la ligne d'origine et la saisie (corps du PATCH).
function changements(r, d) {
  const ch = {};
  const champs = r.type === "externe" ? [...CHAMPS_FICHE, ...CHAMPS_EXTERNE] : CHAMPS_FICHE;
  champs.forEach((k) => {
    if ((r[k] || "") !== (d[k] || "")) ch[k] = d[k] || null;
  });
  const a = [...(r.acces || [])].sort().join();
  const b = [...(d.acces || [])].sort().join();
  if (a !== b) ch.acces = d.acces || [];
  return ch;
}

const SiStatus = ({ r }) => {
  if (r.statut === "arrivee") return <span className="si-st is-in"><Icon name="plus" size={11} />Arrivée · {fmtD(r.dateArrivee)}</span>;
  if (r.statut === "sortie") return <span className="si-st is-out"><Icon name="logout" size={11} />Sortie · {fmtD(r.dateSortie)}</span>;
  if (r.statut === "sorti") return <span className="si-st is-gone">Sorti · {fmtD(r.dateSortie)}</span>;
  return <span className="si-st is-on">Actif</span>;
};

// Écart avec le compte MyALFA (archivé ou supprimé dans Admin).
const BadgeCompte = ({ r }) => {
  if (r.compte === "archive") return <em className="si-ext six-compte">Compte archivé</em>;
  if (r.compte === "supprime") return <em className="si-ext six-compte">Compte supprimé</em>;
  return null;
};

/* ---------- Fenêtre de détail ---------- */

const SiModal = ({ r, isNew, outils, sites, services, onClose, onSaved, onDeleted, notifier }) => {
  const [d, setD] = React.useState(r);
  const [busy, setBusy] = React.useState(false);
  const [confirmerSuppr, setConfirmerSuppr] = React.useState(false);
  const [historique, setHistorique] = React.useState(isNew ? [] : null);
  const set = (p) => setD((x) => ({ ...x, ...p }));
  const externe = d.type === "externe";
  const todo = aFaire(d.statut, d, outils);
  const peut = !externe || (d.nom || "").trim();

  React.useEffect(() => {
    if (isNew) return undefined;
    let actif = true;
    siApi
      .historique(r.ref)
      .then((h) => actif && setHistorique(h))
      .catch(() => actif && setHistorique([]));
    return () => {
      actif = false;
    };
  }, [isNew, r.ref]);

  React.useEffect(() => {
    const echap = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [busy, onClose]);

  // Enregistre les champs modifiés, puis applique la transition demandée.
  const go = async (action, message, corpsAction) => {
    setBusy(true);
    try {
      if (isNew) {
        const cree = await siApi.creerExterne({
          nom: d.nom, prenom: d.prenom, fonction: d.fonction, site: d.site, service: d.service, domaine: d.domaine,
          mail: d.mail, rustdesk: d.rustdesk, refInterne: d.refInterne, nomPc: d.nomPc, acces: d.acces, dateArrivee: d.dateArrivee || null, note: d.note,
        });
        onSaved(cree, "Utilisateur hors système ajouté");
        return;
      }
      let ligne = r;
      const ch = changements(r, d);
      if (Object.keys(ch).length) ligne = await siApi.modifier(r.ref, ch);
      if (action) ligne = await siApi.transition(r.ref, action, corpsAction);
      onSaved(ligne, action ? `${ligne.nomComplet} · ${message.toLowerCase()}` : "Modifications enregistrées", ligne.avertissement);
    } catch (e) {
      notifier(e.message, { type: "erreur" });
      setBusy(false);
    }
  };

  const supprimer = async () => {
    setBusy(true);
    try {
      await siApi.supprimerExterne(r.ref);
      onDeleted(r.ref);
    } catch (e) {
      notifier(e.message, { type: "erreur" });
      setBusy(false);
    }
  };

  const titre = isNew ? "Ajouter un utilisateur hors système" : externe ? nomExterne(d) || r.nomComplet : r.nomComplet;
  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="mf-modal mf-modal-md" role="dialog" aria-modal="true" aria-label={titre}>
        <div className="mf-head">
          <div>
            <div className="mf-meta">
              <b>{externe ? "Utilisateur hors système" : "Utilisateur MyALFA Worksync"}</b>
              {!isNew && <><i></i><SiStatus r={r} /></>}
              {!isNew && <BadgeCompte r={r} />}
            </div>
            <h2>{titre}</h2>
          </div>
          <div className="mf-actions">
            <button className="mf-x" onClick={onClose} title="Fermer" disabled={busy}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="mf-body">
          {externe && (
            <div className="si-ext-note"><Icon name="info" size={14} />Cet utilisateur n'a pas de compte MyALFA Worksync. Il est suivi ici uniquement pour ses accès informatiques.</div>
          )}
          {r.compte === "archive" && (
            <div className="si-ext-note"><Icon name="info" size={14} />Le compte MyALFA de cette personne est archivé. Révoquez ses accès puis clôturez la sortie.</div>
          )}
          <div className="mf-card">
            <div className="mf-card-h"><span className="mf-ico"><Icon name="user" size={14} /></span>Identité</div>
            <div className="si-form">
              {externe ? (
                <>
                  <label><span>Nom</span><input className="mf-input" value={d.nom || ""} onChange={(e) => set({ nom: e.target.value })} /></label>
                  <label><span>Prénom</span><input className="mf-input" value={d.prenom || ""} onChange={(e) => set({ prenom: e.target.value })} /></label>
                  <label className="si-f2"><span>Fonction / organisation</span><input className="mf-input" value={d.fonction || ""} onChange={(e) => set({ fonction: e.target.value })} placeholder="Prestataire, intérimaire, stagiaire école…" /></label>
                </>
              ) : (
                <label className="si-f2"><span>Fonction</span><b>{d.fonction || "—"}</b></label>
              )}
              <label><span>Site</span><select className="mf-input" value={d.site || ""} onChange={(e) => set({ site: e.target.value })}><option value="">À définir</option>{sites.map((x) => <option key={x}>{x}</option>)}</select></label>
              <label><span>Service</span><select className="mf-input" value={d.service || ""} onChange={(e) => set({ service: e.target.value })}><option value="">À définir</option>{services.map((x) => <option key={x}>{x}</option>)}</select></label>
              <label><span>Date d'arrivée</span><input type="date" className="mf-input" value={d.dateArrivee || ""} onChange={(e) => set({ dateArrivee: e.target.value })} /></label>
              <label><span>Date de sortie</span><input type="date" className="mf-input" value={d.dateSortie || ""} onChange={(e) => set({ dateSortie: e.target.value })} /></label>
            </div>
          </div>
          <div className="mf-card">
            <div className="mf-card-h"><span className="mf-ico"><Icon name="shield" size={14} /></span>Identifiants</div>
            <div className="si-form">
              <label>
                <span>Domaine</span>
                <input className="mf-input si-mono" value={d.domaine || ""} onChange={(e) => set({ domaine: e.target.value })} placeholder={r.domaineSuggere || "ALFA\\identifiant"} />
                {!d.domaine && r.domaineSuggere && (
                  <button type="button" className="mf-link six-suggestion" onClick={() => set({ domaine: r.domaineSuggere })}>
                    Utiliser {r.domaineSuggere}
                  </button>
                )}
              </label>
              <label><span>RustDesk (n°)</span><input className="mf-input si-mono" value={d.rustdesk || ""} onChange={(e) => set({ rustdesk: e.target.value })} placeholder="123 456 789" /></label>
              <label><span>Réf interne</span><input className="mf-input si-mono" value={d.refInterne || ""} onChange={(e) => set({ refInterne: e.target.value })} /></label>
              <label><span>Nom PC</span><input className="mf-input si-mono" value={d.nomPc || ""} onChange={(e) => set({ nomPc: e.target.value })} placeholder="Nom de l'ordinateur" /></label>
              <label className="si-f2"><span>Mail</span><input className="mf-input" value={d.mail || ""} onChange={(e) => set({ mail: e.target.value })} placeholder="prenom@alfacomptabilite.com" /></label>
            </div>
          </div>
          <div className="mf-card">
            <div className="mf-card-h"><span className="mf-ico"><Icon name="check" size={14} /></span>Accès logiciels
              {d.statut === "arrivee" && <span className="si-hint is-in">{todo > 0 ? `${todo} élément${todo > 1 ? "s" : ""} à mettre en place` : "Arrivée prête à être validée"}</span>}
              {d.statut === "sortie" && <span className="si-hint is-out">{todo > 0 ? `${todo} accès à révoquer` : "Tous les accès sont révoqués"}</span>}
            </div>
            <div className="si-acc">
              {outils.map((t) => {
                const on = (d.acces || []).includes(t.cle);
                const cls = d.statut === "sortie" && on ? "is-revoke" : d.statut === "arrivee" && !on ? "is-missing" : "";
                return (
                  <label key={t.cle} className={`si-acc-i ${on ? "is-on" : ""} ${cls}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => set({ acces: e.target.checked ? [...(d.acces || []), t.cle] : (d.acces || []).filter((k) => k !== t.cle) })}
                    />
                    <span>{t.libelle}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="mf-card">
            <div className="mf-card-h"><span className="mf-ico"><Icon name="fileText" size={14} /></span>Note</div>
            <textarea className="mf-input mf-note" value={d.note || ""} onChange={(e) => set({ note: e.target.value })} placeholder="Poste, matériel remis, particularités…"></textarea>
          </div>
          {!isNew && (historique === null || historique.length > 0) && (
            <div className="mf-card">
              <div className="mf-card-h"><span className="mf-ico"><Icon name="list" size={14} /></span>Historique</div>
              <div className="si-log">
                {historique === null
                  ? [0, 1].map((i) => <div key={i}><span className="six-sk" style={{ width: 110 }} /><span className="six-sk" style={{ width: 90 }} /><span className="six-sk" style={{ width: 180 }} /></div>)
                  : historique.map((l, i) => <div key={i}><span>{fmtTs(l.date)}</span><b>{l.auteur}</b><em>{l.texte}</em></div>)}
              </div>
            </div>
          )}
        </div>
        <div className="mf-foot si-foot">
          {externe && !isNew && (confirmerSuppr ? (
            <>
              <span className="six-confirm">Supprimer cet utilisateur ?</span>
              <button className="btn si-btn-del" onClick={supprimer} disabled={busy}><Icon name="trash" size={14} /> Supprimer</button>
              <button className="btn" onClick={() => setConfirmerSuppr(false)} disabled={busy}>Non</button>
            </>
          ) : (
            <button className="btn si-btn-del" onClick={() => setConfirmerSuppr(true)} disabled={busy}><Icon name="trash" size={14} /> Supprimer</button>
          ))}
          <span style={{ flex: 1 }}></span>
          <button className="btn" onClick={onClose} disabled={busy}>Annuler</button>
          {!isNew && r.statut === "actif" && <button className="btn si-btn-out" disabled={busy} onClick={() => go("declarer-sortie", "Sortie déclarée", { date: d.dateSortie || aujourdhui() })}><Icon name="logout" size={14} /> Déclarer la sortie</button>}
          {!isNew && r.statut === "sortie" && r.compte !== "archive" && r.compte !== "supprime" && <button className="btn" disabled={busy} onClick={() => go("annuler-sortie", "Sortie annulée")}>Annuler la sortie</button>}
          {!isNew && r.statut === "sorti" && r.compte !== "archive" && r.compte !== "supprime" && <button className="btn" disabled={busy} onClick={() => go("reactiver", "Utilisateur réactivé")}>Réactiver</button>}
          {!isNew && r.statut === "arrivee" && <button className="btn tk-btn-ok" disabled={busy || todo > 0} title={todo > 0 ? "Tous les accès et identifiants doivent être renseignés" : ""} onClick={() => go("valider-arrivee", "Arrivée validée")}><Icon name="check" size={14} /> Valider l'arrivée</button>}
          {!isNew && r.statut === "sortie" && <button className="btn tk-btn-ok" disabled={busy || todo > 0} title={todo > 0 ? "Tous les accès doivent être révoqués" : ""} onClick={() => go("cloturer-sortie", "Sortie clôturée")}><Icon name="check" size={14} /> Clôturer la sortie</button>}
          <button className="btn primary" disabled={!peut || busy} onClick={() => go(null)}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Tableau ---------- */

const LigneSquelette = ({ nb }) => (
  <tr>
    <td><span className="lm-collab si-user"><span className="lm-av six-sk-av" /><span className="tk-user-t"><span className="six-sk" style={{ width: 120 }} /><small className="six-sk" style={{ width: 80 }} /></span></span></td>
    {Array.from({ length: nb }).map((_, i) => <td key={i}><span className="six-sk" style={{ width: i < 7 ? 70 : 14 }} /></td>)}
  </tr>
);

export default function SiUtilisateurs({ lignes, setLignes, outils, sites, services, chargement, notifier }) {
  const [q, setQ] = React.useState("");
  const [view, setView] = React.useState("all");
  const [fSite, setFSite] = React.useState("");
  const [fSvc, setFSvc] = React.useState("");
  const [open, setOpen] = React.useState(null);
  const [edit, setEdit] = React.useState(null);
  const setE = (p) => setEdit((e) => ({ ...e, ...p }));

  const remplacer = (l) => setLignes((ls) => (ls.some((x) => x.ref === l.ref) ? ls.map((x) => (x.ref === l.ref ? l : x)) : [...ls, l]).sort(trier));
  const avecAFaire = (l) => ({ ...l, aFaire: aFaire(l.statut, l, outils) });

  // Modification sur la ligne : affichée tout de suite, annulée si refusée.
  const commitEdit = async (r) => {
    const ch = changements(r, { ...r, ...edit });
    setEdit(null);
    if (!Object.keys(ch).length) return;
    remplacer(avecAFaire({ ...r, ...ch }));
    try {
      remplacer(await siApi.modifier(r.ref, ch));
      notifier("Modifications enregistrées");
    } catch (e) {
      remplacer(r);
      notifier(e.message, { type: "erreur" });
    }
  };

  const quick = async (r, action, statut, msg) => {
    remplacer(avecAFaire({ ...r, statut }));
    try {
      const l = await siApi.transition(r.ref, action);
      remplacer(l);
      notifier(`${r.nomComplet} · ${msg.toLowerCase()}`, { info: l.avertissement });
    } catch (e) {
      remplacer(r);
      notifier(e.message, { type: "erreur" });
    }
  };

  const live = lignes.filter((r) => r.statut !== "sorti");
  const cnt = {
    all: live.length,
    actif: live.filter((r) => r.statut === "actif").length,
    arrivee: live.filter((r) => r.statut === "arrivee").length,
    sortie: live.filter((r) => r.statut === "sortie").length,
    ext: live.filter((r) => r.type === "externe").length,
    sorti: lignes.filter((r) => r.statut === "sorti").length,
  };
  const ql = q.trim().toLowerCase();
  const list = (view === "sorti" ? lignes.filter((r) => r.statut === "sorti") : live.filter((r) => view === "all" || (view === "ext" ? r.type === "externe" : r.statut === view)))
    .filter((r) => (!fSite || r.site === fSite) && (!fSvc || r.service === fSvc))
    .filter((r) => !ql || [r.nomComplet, r.fonction, r.site, r.service, r.domaine, r.mail, r.rustdesk, r.refInterne, r.nomPc].join(" ").toLowerCase().includes(ql));

  const nouvelExterne = () =>
    setOpen({
      isNew: true,
      r: { ref: "nouveau", type: "externe", nom: "", prenom: "", fonction: "", site: "", service: "", domaine: "", mail: "", rustdesk: "", refInterne: "", nomPc: "", acces: [], statut: "arrivee", dateArrivee: aujourdhui(), dateSortie: "", note: "" },
    });
  const openR = open && (open.isNew ? open.r : lignes.find((r) => r.ref === open.ref));
  const nbCols = outils.length + 8;

  return (
    <>
      <div className="lm-bar">
        <label className="mdt-search">
          <Icon name="search" size={13} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un utilisateur, un identifiant, un n° RustDesk, un PC…" aria-label="Rechercher" />
        </label>
        <div className="tk-seg" role="group" aria-label="Filtrer par statut">
          {[["all", "Tous"], ["actif", "Actifs"], ["arrivee", "Arrivées"], ["sortie", "Sorties"], ["ext", "Hors système"]].map(([k, l]) => (
            <button key={k} className={`${view === k ? "is-on" : ""} si-seg-${k}`} onClick={() => setView(k)} aria-pressed={view === k}>{l}<span>{cnt[k]}</span></button>
          ))}
        </div>
        <select className="mf-input si-fsel" value={fSite} onChange={(e) => setFSite(e.target.value)} aria-label="Site"><option value="">Tous les sites</option>{sites.map((x) => <option key={x}>{x}</option>)}</select>
        <select className="mf-input si-fsel" value={fSvc} onChange={(e) => setFSvc(e.target.value)} aria-label="Service"><option value="">Tous les services</option>{services.map((x) => <option key={x}>{x}</option>)}</select>
        <span style={{ flex: 1 }}></span>
        <button className={`btn ${view === "sorti" ? "is-on" : ""}`} onClick={() => setView(view === "sorti" ? "all" : "sorti")}><Icon name="archive" size={15} /> {view === "sorti" ? "Retour à la liste" : `Sortis (${cnt.sorti})`}</button>
        <button className="btn primary" onClick={nouvelExterne}><Icon name="plus" size={15} /> Utilisateur hors système</button>
      </div>
      <div className="mdt-wrap">
        <table className="mdt-t si-t">
          <thead>
            <tr>
              <th>Utilisateur</th><th>Site · Service</th><th>Statut</th><th>Domaine</th><th>Mail</th><th>RustDesk</th><th>Réf interne</th><th>Nom PC</th>
              {outils.map((t) => <th key={t.cle} className="si-c">{t.libelle}</th>)}
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {chargement && Array.from({ length: 8 }).map((_, i) => <LigneSquelette key={i} nb={nbCols} />)}
            {!chargement && list.map((r) => {
              const todo = r.aFaire;
              const ed = edit && edit.ref === r.ref ? edit : null;
              const v = ed || r;
              return (
                <tr key={r.ref} className={`si-row is-${r.statut} ${r.type === "externe" ? "is-ext" : ""} ${ed ? "is-editing" : ""}`} onClick={() => { if (!ed) setOpen({ ref: r.ref }); }}>
                  <td>
                    <span className="lm-collab si-user">
                      <span className={`lm-av ${r.type === "externe" ? "si-av-ext" : ""}`}>{initiales(r.nomComplet)}</span>
                      <span className="tk-user-t">
                        <span>{r.nomComplet}{r.type === "externe" && <em className="si-ext">Hors système</em>}<BadgeCompte r={r} /></span>
                        <small>{r.fonction || "—"}</small>
                      </span>
                    </span>
                  </td>
                  {ed ? (
                    <td onClick={(e) => e.stopPropagation()}><div className="si-org-ed">
                      <select className="mf-input si-in" value={ed.site || ""} onChange={(e) => setE({ site: e.target.value })} aria-label="Site"><option value="">Site…</option>{sites.map((x) => <option key={x}>{x}</option>)}</select>
                      <select className="mf-input si-in" value={ed.service || ""} onChange={(e) => setE({ service: e.target.value })} aria-label="Service"><option value="">Service…</option>{services.map((x) => <option key={x}>{x}</option>)}</select>
                    </div></td>
                  ) : (
                    <td><span className="si-org"><b>{r.site || <span className="si-miss">Site ?</span>}</b><small>{r.service || "Service ?"}</small></span></td>
                  )}
                  <td><SiStatus r={r} /></td>
                  {ed ? (
                    <>
                      <td onClick={(e) => e.stopPropagation()}><input className="mf-input si-in si-mono" value={ed.domaine || ""} onChange={(e) => setE({ domaine: e.target.value })} placeholder={r.domaineSuggere || "ALFA\\identifiant"} aria-label="Domaine" /></td>
                      <td onClick={(e) => e.stopPropagation()}><input className="mf-input si-in" value={ed.mail || ""} onChange={(e) => setE({ mail: e.target.value })} placeholder="Mail" aria-label="Mail" /></td>
                      <td onClick={(e) => e.stopPropagation()}><input className="mf-input si-in si-in-s si-mono" value={ed.rustdesk || ""} onChange={(e) => setE({ rustdesk: e.target.value })} placeholder="N°" aria-label="RustDesk" /></td>
                      <td onClick={(e) => e.stopPropagation()}><input className="mf-input si-in si-in-s si-mono" value={ed.refInterne || ""} onChange={(e) => setE({ refInterne: e.target.value })} placeholder="Réf." aria-label="Réf interne" /></td>
                      <td onClick={(e) => e.stopPropagation()}><input className="mf-input si-in si-in-s si-mono" value={ed.nomPc || ""} onChange={(e) => setE({ nomPc: e.target.value })} placeholder="Nom PC" aria-label="Nom PC" /></td>
                    </>
                  ) : (
                    <>
                      <td className="si-mono" title={!r.domaine && r.domaineSuggere ? `Suggestion : ${r.domaineSuggere}` : undefined}>{r.domaine || <span className="si-miss">À créer</span>}</td>
                      <td className="si-mail">{r.mail || <span className="mdt-muted">—</span>}</td>
                      <td className="si-mono">{r.rustdesk || (r.statut === "arrivee" ? <span className="si-miss">À installer</span> : <span className="mdt-muted">—</span>)}</td>
                      <td className="si-mono">{r.refInterne || <span className="mdt-muted">—</span>}</td>
                      <td className="si-mono">{r.nomPc || <span className="mdt-muted">—</span>}</td>
                    </>
                  )}
                  {outils.map((t) => {
                    const on = (v.acces || []).includes(t.cle);
                    const cls = r.statut === "sortie" && on ? "is-revoke" : r.statut === "arrivee" && !on ? "is-missing" : "";
                    const tip = `${t.libelle} · ${on ? (r.statut === "sortie" ? "à révoquer" : "accès ouvert") : r.statut === "arrivee" ? "à ouvrir" : "pas d'accès"}`;
                    return (
                      <td key={t.cle} className="si-c" onClick={ed ? (e) => e.stopPropagation() : undefined}>
                        {ed ? (
                          <button className={`si-box ${on ? "is-on" : ""} ${cls}`} title={tip} aria-label={tip} aria-pressed={on} onClick={() => setE({ acces: on ? ed.acces.filter((k) => k !== t.cle) : [...ed.acces, t.cle] })}>{on && <Icon name="check" size={12} />}</button>
                        ) : (
                          <span className={`si-mark ${on ? "is-on" : ""} ${cls}`} title={tip}>{on ? <Icon name="check" size={13} /> : <i></i>}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="lm-act" onClick={(e) => e.stopPropagation()}>
                    {ed ? (
                      <>
                        <button className="tk-close-btn" onClick={() => commitEdit(r)}><Icon name="check" size={13} /> Enregistrer</button>
                        <button className="mdt-ic" title="Annuler" aria-label="Annuler" onClick={() => setEdit(null)}><Icon name="x" size={13} /></button>
                      </>
                    ) : (
                      <>
                        {r.statut === "arrivee" && (todo > 0 ? <span className="si-todo is-in">{todo} à faire</span> : <button className="tk-close-btn" onClick={() => quick(r, "valider-arrivee", "actif", "Arrivée validée")}><Icon name="check" size={13} /> Valider</button>)}
                        {r.statut === "sortie" && (todo > 0 ? <span className="si-todo is-out">{todo} à révoquer</span> : <button className="tk-close-btn" onClick={() => quick(r, "cloturer-sortie", "sorti", "Sortie clôturée")}><Icon name="check" size={13} /> Clôturer</button>)}
                        {r.statut !== "sorti" && (
                          <button className="mdt-ic eye" title="Modifier la ligne" aria-label="Modifier la ligne" onClick={() => setEdit({ ref: r.ref, site: r.site || "", service: r.service || "", domaine: r.domaine || "", mail: r.mail || "", rustdesk: r.rustdesk || "", refInterne: r.refInterne || "", nomPc: r.nomPc || "", acces: [...(r.acces || [])] })}>
                            <Icon name="pencil" size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!chargement && list.length === 0 && <tr><td colSpan={nbCols + 1} className="mdt-empty">Aucun utilisateur ne correspond aux critères</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mdt-foot">
        <span>{chargement ? "Chargement…" : `${list.length} utilisateur${list.length > 1 ? "s" : ""} affiché${list.length > 1 ? "s" : ""}`}</span>
        <span className="si-legend">
          <span className="si-mark is-missing"><i></i></span>Accès à ouvrir (arrivée)
          <span className="si-mark is-on is-revoke"><Icon name="check" size={12} /></span>Accès à révoquer (sortie)
          <span className="si-legend-h"><Icon name="pencil" size={11} />pour modifier une ligne</span>
        </span>
      </div>
      {openR && (
        <SiModal
          key={openR.ref}
          r={openR}
          isNew={!!open.isNew}
          outils={outils}
          sites={sites}
          services={services}
          notifier={notifier}
          onClose={() => setOpen(null)}
          onDeleted={(ref) => {
            setLignes((ls) => ls.filter((x) => x.ref !== ref));
            setOpen(null);
            notifier("Utilisateur hors système supprimé");
          }}
          onSaved={(l, msg, info) => {
            remplacer(l);
            setOpen(null);
            notifier(msg, { info });
          }}
        />
      )}
    </>
  );
}
